// Hideo - what happens while the app loads and when the page is ready (docs/architecture.md, "Boot sequence").
// Ported from the old glue that ran while the app loaded: src/legacy-bridge.ts (stores, saving listeners) and the legacy
// core/utils.js and core/app.js (file-drop guard, keyboard, DOMContentLoaded); verbatim copies in tests/fixtures/legacy
// (app.js, utils-w12.js, head/legacy-bridge.ts.txt), golden tests in tests/boot.test.ts.
import { applyI18n } from '../i18n'
import { app } from './app'
import { isLegalPath } from '../navigation'
import { installKeyboard } from './keyboard'
import { installImport } from './storage/files'
import { installAutosync } from './cloud/autosync'
import { installFileDropGuard } from '../utils/image'
import { uiAlert } from '../stores/notices'
import { useDataStore } from '../stores/data'
import { useUiStore } from '../stores/ui'
import { useProjectsStore } from '../stores/projects'
import { useTagStore } from '../stores/tags'
import { useScriptsStore } from '../stores/scripts'
import { useEditorStore } from '../stores/editor'
import { useTeleprompterStore } from '../stores/teleprompter'
import { useThemeStore } from '../stores/theme'
import { useSettingsStore } from '../stores/settings'
import { useTourStore } from '../stores/tour'
import { usePedStore } from '../stores/ped'
import { useDialogsStore } from '../stores/dialogs'
import { usePickerStore } from '../stores/picker'
import { useCloudStore } from '../stores/cloud'
import { useSyncStore } from '../stores/sync'
import { useCreatorsStore } from '../stores/creators'
import { useVideoStore } from '../stores/video'
import { installSky } from './sky'
import { installSounds } from '../stores/sounds'
import { isDesktop } from './desktop'

/** Installable app (PWA): the service worker, only from the online website (the browser does not allow it from file://;
 *  the desktop app has its files already, src/features/desktop.ts) */
function registerServiceWorker(nav: Navigator = navigator, loc: Pick<Location, 'protocol'> = location): void {
  if ('serviceWorker' in nav && /^https?:$/.test(loc.protocol) && !isDesktop()) nav.serviceWorker.register('/sw.js').catch(e => console.warn('Service worker not registered', e))
}

/** The page is ready (DOMContentLoaded): static markup translated, theme and main colour with the system theme followed,
 *  the app theme's background and sounds (src/features/sky.ts, src/stores/sounds.ts: listeners that only listen, so they
 *  can come after the Vue components'), the app started under the profile picker when a profile exists, the picker shown (always, at start-up), the service
 *  worker. Opened on a legal page (/privacy…, e.g. from Google's consent screen) neither the app nor the picker start:
 *  the page is readable at once, and the app starts when it is entered from there (src/features/legal.ts) */
export function onReady(): void {
  applyI18n(document)
  useThemeStore().init()
  installSky(window, useThemeStore())
  installSounds(document)
  if (!isLegalPath(location.pathname)) {
    app.boot()
    usePickerStore().show()
  }
  registerServiceWorker()
}

/** Everything that happens while the app loads, before the Vue app is mounted (called once by src/main.ts), in the
 *  order the old glue did it: alert() becomes the app's message; the stores are created (their setup reads this
 *  device's stored state: preferences, filters, saving state); the saving's listeners (#importJsonFile, leaving the
 *  page, back online, the periodic online check); the file-drop guard on the window; the global shortcuts; the start at
 *  DOMContentLoaded. Their listeners come before the @ menu's and the editor's (installed next by main.ts) and the Vue
 *  components' */
export function loadApp(doc: Document = document, win: Window = window): void {
  win.alert = uiAlert   // non-blocking: the code after it goes on at once
  useDataStore(); useUiStore(); useProjectsStore(); useTagStore(); useScriptsStore(); useEditorStore(); useTeleprompterStore()
  useThemeStore(); useSettingsStore(); useTourStore(); usePedStore(); useDialogsStore(); usePickerStore(); useCloudStore()
  useSyncStore()
  installImport(doc)
  installAutosync(doc, win)
  useCreatorsStore(); useVideoStore()
  installFileDropGuard(win)
  installKeyboard(doc)
  doc.addEventListener('DOMContentLoaded', onReady)
}
